<template>
  <div class="p-4 md:p-6 space-y-4">
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div>
        <h1 class="text-xl font-extrabold text-slate-800 text-left">Jobs</h1>
        <p class="text-xs text-slate-500 text-left">Approve or reject submitted postings</p>
      </div>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          type="button"
          class="px-3 py-1.5 rounded-full text-xs font-bold border transition"
          :class="statusFilter === tab.value
            ? 'bg-[#0d4169] text-white border-[#0d4169]'
            : 'bg-white text-slate-600 border-slate-200 hover:border-[#0d4169]/40'"
          @click="setFilter(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center py-16">
      <div class="h-8 w-8 border-4 border-gray-300 border-t-[#0d4169] rounded-full animate-spin"></div>
    </div>

    <p v-else-if="!jobs.length" class="text-sm text-slate-500 text-center py-10">No jobs found for this filter.</p>

    <div v-else class="grid gap-3">
      <article
        v-for="job in jobs"
        :key="job.id"
        class="card-panel p-4 md:p-5"
      >
        <div class="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div class="text-left min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2 mb-1">
              <h2 class="text-base font-bold text-slate-800">{{ formatJobTitles(job.title) }}</h2>
              <span class="status-pill" :class="statusClass(job)">{{ getJobDisplayStatus(job) }}</span>
            </div>
            <p class="text-xs text-slate-500">{{ formatJobMeta(job) }}</p>
            <p v-if="job.company_name || job.work_location" class="text-xs text-slate-500 mt-0.5">
              <span v-if="job.company_name">{{ job.company_name }}</span>
              <span v-if="job.company_name && job.work_location"> · </span>
              <span v-if="job.work_location">{{ job.work_location }}</span>
            </p>
            <p class="text-sm text-slate-600 mt-2 line-clamp-2">{{ job.description }}</p>
            <p class="text-[11px] text-slate-400 mt-2">
              Employer: {{ job.employer_name || '—' }}
              <span v-if="employerUsername(job)"> · @{{ employerUsername(job) }}</span>
              <span v-if="job.employer_phone"> · {{ job.employer_phone }}</span>
              · {{ formatDate(job.created_at) }}
            </p>
          </div>

          <div class="flex flex-wrap gap-2 shrink-0">
            <button type="button" class="btn-primary" @click="$router.push({ name: 'job_review', params: { id: job.id } })">
              Open
            </button>
            <button
              v-if="job.status === 'pending'"
              type="button"
              class="btn-success"
              :disabled="busyId === job.id"
              @click="approve(job)"
            >
              Approve
            </button>
            <button
              v-if="job.status === 'pending'"
              type="button"
              class="btn-danger"
              :disabled="busyId === job.id"
              @click="reject(job)"
            >
              Reject
            </button>
          </div>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { apiFetch } from '../utils/api'
import { formatJobMeta, formatJobTitles, getJobDisplayStatus } from '../utils/jobFields'

export default {
  name: 'JobsView',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const jobs = ref([])
    const loading = ref(true)
    const busyId = ref(null)

    const tabs = [
      { label: 'All', value: '' },
      { label: 'Pending', value: 'pending' },
      { label: 'Published', value: 'published' },
      { label: 'Rejected', value: 'rejected' },
    ]

    const statusFilter = computed(() => String(route.query.status || ''))

    function setFilter(value) {
      router.replace({ name: 'jobs', query: value ? { status: value } : {} })
    }

    function statusClass(job) {
      const status = getJobDisplayStatus(job)
      if (status === 'expired') return 'bg-slate-200 text-slate-600'
      if (status === 'pending') return 'bg-amber-100 text-amber-800'
      if (status === 'published') return 'bg-emerald-100 text-emerald-800'
      return 'bg-rose-100 text-rose-700'
    }

    function formatDate(value) {
      return value ? new Date(value).toLocaleString() : ''
    }

    function employerUsername(job) {
      const u = job?.employer_username || job?.telegram_username
      return u ? String(u).replace(/^@/, '') : ''
    }

    async function load() {
      loading.value = true
      try {
        const q = statusFilter.value ? `?status=${encodeURIComponent(statusFilter.value)}` : ''
        const res = await apiFetch(`/jobs${q}`)
        const data = await res.json()
        jobs.value = data.jobs || []
      } catch (err) {
        console.error(err)
      } finally {
        loading.value = false
      }
    }

    async function approve(job) {
      busyId.value = job.id
      try {
        const res = await apiFetch(`/jobs/${job.id}/approve`, { method: 'POST' })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Approve failed')
        await load()
      } catch (err) {
        alert(err.message)
      } finally {
        busyId.value = null
      }
    }

    async function reject(job) {
      if (!confirm(`Reject "${formatJobTitles(job.title)}"?`)) return
      busyId.value = job.id
      try {
        const res = await apiFetch(`/jobs/${job.id}/reject`, { method: 'POST' })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Reject failed')
        await load()
      } catch (err) {
        alert(err.message)
      } finally {
        busyId.value = null
      }
    }

    watch(statusFilter, load)
    onMounted(load)

    return {
      jobs,
      loading,
      busyId,
      tabs,
      statusFilter,
      setFilter,
      statusClass,
      formatDate,
      employerUsername,
      formatJobTitles,
      formatJobMeta,
      getJobDisplayStatus,
      approve,
      reject,
    }
  },
}
</script>

<style scoped>
.status-pill {
  @apply inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
